import React, { useState, useEffect } from "react";

const BulletinCard = ({ bulletin }) => {
  const [imageErrors, setImageErrors] = useState({ 
    logoEcole: false, 
    logoPays: false 
  });
  
  const [formattedNotes, setFormattedNotes] = useState([]);
  const [isPrintMode, setIsPrintMode] = useState(false);
  
  // Détecter le mode impression
  useEffect(() => {
    const handleBeforePrint = () => setIsPrintMode(true);
    const handleAfterPrint = () => setIsPrintMode(false);
    
    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);
    
    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, []);
  
  // Formater les notes pour optimiser l'affichage PDF
  useEffect(() => {
    if (bulletin?.notes) {
      const formatted = bulletin.notes.map(note => ({
        ...note,
        valeur: formatMoyenne(note.valeur),
        displayNote: formatMoyenneForDisplay(note.valeur)
      }));
      setFormattedNotes(formatted);
    }
  }, [bulletin]);

  const handleImageError = (type) =>
    setImageErrors((prev) => ({ ...prev, [type]: true }));
  
  const getImageUrl = (path) => path || null;

  const formatMoyenne = (m) => {
    if (m === null || m === undefined || m === "") return "0.00";
    const num = parseFloat(m);
    return isNaN(num) ? "0.00" : num.toFixed(2);
  };

  const formatMoyenneForDisplay = (m) => {
    if (m === null || m === undefined || m === "") return "0,00";
    const num = parseFloat(m);
    return isNaN(num) ? "0,00" : num.toFixed(2).replace('.', ',');
  };

  // Utiliser les appréciations du serveur ou valeurs par défaut
  const getAppreciationEducateur = () => {
    return bulletin?.appreciation_educateur || "Non évalué";
  };

  const getAppreciationEcole = () => {
    return bulletin?.appreciation_ecole || "Votre enfant doit redoubler d'efforts";
  };

  // Formater la date de naissance
  const formatDateNaissance = (dateStr) => {
    if (!dateStr) return "Non renseignée";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString('fr-FR');
    } catch (e) {
      return dateStr;
    }
  };

  // Calcul de la moyenne générale
  const calculerMoyenne = () => {
    // Priorité 1 : moyenne_generale du bulletin
    if (bulletin?.moyenne_generale !== undefined && bulletin.moyenne_generale !== null && bulletin.moyenne_generale !== "") {
      const moy = parseFloat(bulletin.moyenne_generale);
      return isNaN(moy) ? 0 : moy;
    }
    
    // Priorité 2 : calcul à partir des notes
    if (bulletin?.notes && bulletin.notes.length > 0) {
      const notesValides = bulletin.notes.filter(n => {
        const valeur = parseFloat(n.valeur);
        return !isNaN(valeur);
      });
      
      if (notesValides.length === 0) return 0;
      
      const somme = notesValides.reduce((acc, note) => {
        const coef = note.coefficient || 1;
        return acc + (parseFloat(note.valeur) * coef);
      }, 0);
      
      const totalCoef = notesValides.reduce((acc, note) => {
        return acc + (note.coefficient || 1);
      }, 0);
      
      return totalCoef > 0 ? somme / totalCoef : 0;
    }
    
    // Valeur par défaut
    return 0;
  };

  const moyenneGenerale = calculerMoyenne();
  const moyenneFormatee = formatMoyenneForDisplay(moyenneGenerale);
  const noteMaximale = bulletin?.note_maximale || 10.00;

  // Contenu dynamique depuis la configuration
  const nomEcole = bulletin?.nom_ecole || "Ecole Primaire Privée KAMALPHA";
  const sloganEcole = bulletin?.slogan_ecole || "L'innovation numérique au cœur de l'éducation";
  const deviseEcole = bulletin?.devise_ecole || "DÉTERMINATION – DISCIPLINE – SUCCÈS";
  const adresseComplete = bulletin?.adresse_complete || "BP : 532 Bobo-Dioulasso – Tél : 60 01 30 10";
  const siteWeb = bulletin?.site_web || "https://www.kamalpha.org";
  const emailEcole = bulletin?.email || "centrekamalpha@gmail.com";
  const texteLogoPays = bulletin?.texte_logo_pays || "BURKINA FASO";
  const sousTexteLogoPays = bulletin?.sous_texte_logo_pays || "Unité – Progrès – Justice";

  // Informations élève
  const eleveNom = bulletin?.eleve_nom || "";
  const elevePrenom = bulletin?.eleve_prenom || "";
  const classeNom = bulletin?.classe_nom || "";
  const anneeScolaire = bulletin?.annee_scolaire || "";
  const trimestreNom = bulletin?.trimestre_nom || "";
const sousTrimestre = bulletin?.sous_trimestre_nom 
  ? ` ${bulletin.sous_trimestre_nom}` 
  : ` ${trimestreNom}`;
  // Date d'édition
  const dateEdition = bulletin?.date_generation ? 
    new Date(bulletin.date_generation).toLocaleDateString('fr-FR') : 
    new Date().toLocaleDateString('fr-FR');

  // Matières
  const matieres = formattedNotes.length > 0 ? formattedNotes : [];

  // Calculer le nombre de matières pour ajuster dynamiquement
  const nbMatieres = matieres.length;
  
  // Ajustements dynamiques basés sur le nombre de matières
  const getTableStyles = () => {
    if (nbMatieres <= 8) {
      return {
        fontSize: "10pt", 
        rowPadding: "1.5mm 2mm",
        headerFontSize: "10pt", 
        footerFontSize: "14pt", 
        matiereWidth: "70%", 
        noteWidth: "30%" 
      };
    } else if (nbMatieres <= 12) {
      return {
        fontSize: "9pt",
        rowPadding: "1.2mm 2mm",
        headerFontSize: "9pt",
        footerFontSize: "13pt", 
        matiereWidth: "65%", 
        noteWidth: "35%" 
      };
    } else if (nbMatieres <= 16) {
      return {
        fontSize: "8.5pt",
        rowPadding: "1mm 1.5mm",
        headerFontSize: "8.5pt",
        footerFontSize: "12pt", 
        matiereWidth: "60%", 
        noteWidth: "40%" 
      };
    } else {
      return {
        fontSize: "8pt",
        rowPadding: "0.8mm 1.2mm",
        headerFontSize: "8pt",
        footerFontSize: "11pt", 
        matiereWidth: "55%", 
        noteWidth: "45%" 
      };
    }
  };

  const getAppreciationStyles = () => {
    if (nbMatieres <= 12) {
      return {
        fontSize: "9pt",
        minHeight: "25mm"
      };
    } else if (nbMatieres <= 16) {
      return {
        fontSize: "8.5pt",
        minHeight: "20mm"
      };
    } else {
      return {
        fontSize: "8pt",
        minHeight: "18mm"
      };
    }
  };

  const getMoyenneVirtuelleStyles = () => {
    if (nbMatieres <= 12) {
      return {
        width: "40mm", 
        titleFontSize: "8pt", 
        moyenneFontSize: "16pt" 
      };
    } else if (nbMatieres <= 16) {
      return {
        width: "35mm", 
        titleFontSize: "7.5pt", 
        moyenneFontSize: "14pt" 
      };
    } else {
      return {
        width: "30mm", 
        titleFontSize: "7pt", 
        moyenneFontSize: "13pt" 
      };
    }
  };

  const tableStyles = getTableStyles();
  const appreciationStyles = getAppreciationStyles();
  const moyenneVirtuelleStyles = getMoyenneVirtuelleStyles();

  const printStyle = {
    card: {
      width: "210mm",
      minHeight: "297mm",
      margin: "0 auto",
      padding: "10mm",
      backgroundColor: "#FFFFFF",
      fontFamily: "'Times New Roman', 'Roboto', serif",
      color: "#000000",
      position: "relative",
      boxSizing: "border-box",
      border: "none",
      breakInside: "avoid",
      pageBreakInside: "avoid"
    },
    header: {
      textAlign: "center", 
      marginBottom: "6mm",
      borderBottom: "1px solid #0D9488",
      paddingBottom: "3mm"
    },
    tableHeader: {
      backgroundColor: "#0D9488",
      color: "#FFFFFF",
      fontWeight: "bold"
    },
    noteCell: {
      textAlign: "right",
      fontFamily: "'Times New Roman', 'Roboto', serif",
      fontWeight: "bold",
      fontVariantNumeric: "normal"
    }
  };

  return (
    <div
      style={printStyle.card}
      className="bulletin-card"
      data-pdf-page="true"
    >
      <header style={{
        borderBottom: "2px dashed #404242b2",
        paddingBottom: "4mm",
        marginBottom: "6mm"
      }}>
        <div style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "5mm"
        }}>
          {/* Logo école */}
          <div style={{ width: "25mm" }}>
            <img
              src={getImageUrl(bulletin?.logo_ecole_url)}
              alt="Logo École"
              style={{
                width: "25mm",
                height: "25mm",
                objectFit: "contain"
              }}
              onError={() => handleImageError('logoEcole')}
            />
          </div>

          {/* Texte école */}
          <div style={{
            flex: 1,
            textAlign: "left",
            lineHeight: "1.3"
          }}>
            <h1 style={{
              fontSize: "14pt",
              fontWeight: "bold",
              margin: 0,
              textTransform: "uppercase"
            }}>
              {nomEcole}
            </h1>

            <p style={{
              fontSize: "10pt",
              margin: "1mm 0"
            }}>
              {sloganEcole}
            </p>

            <p style={{
              fontSize: "9pt",
              margin: 0
            }}>
              {adresseComplete}
            </p>

            <p style={{
              fontSize: "9pt",
              margin: 0
            }}>
              {siteWeb}
            </p>
          </div>

          {/* Bloc Burkina Faso */}
          <div style={{
            width: "35mm",
            textAlign: "center",
            lineHeight: "1.2"
          }}>
            <div style={{
              fontWeight: "bold",
              fontSize: "9pt"
            }}>
              {texteLogoPays}
            </div>

            <div style={{
              fontSize: "8pt",
              fontStyle: "italic",
              marginBottom: "1mm"
            }}>
              {sousTexteLogoPays}
            </div>

            <img
              src={getImageUrl(bulletin?.logo_pays_url)}
              alt="Armoiries Burkina Faso"
              style={{
                width: "22mm",
                height: "22mm",
                objectFit: "contain"
              }}
              onError={() => handleImageError('logoPays')}
            />
          </div>
        </div>

        {/* Devise */}
        <div style={{
          fontSize: "9pt",
          fontWeight: "bold",
          color: "#0D9488",
          letterSpacing: "0.4mm",
          marginTop: "2mm"
        }}>
          {deviseEcole}
        </div>
      </header>

      {/* TITRE DU BULLETIN */}
      <div style={{ 
        width: "100%",
        textAlign: "center",
        marginTop: "1mm",
        marginBottom: nbMatieres > 12 ? "3mm" : "5mm",
        backgroundColor: "#0D9488",
        color: "#FFFFFF",
        fontWeight: "bold",
        fontSize: nbMatieres > 16 ? "12pt" : "14pt",
        textTransform: "uppercase",
        padding: nbMatieres > 16 ? "1.5mm 0" : "2mm 0",
        border: "1px solid #0A7A6F",
        lineHeight: "1.2",
        boxSizing: "border-box"
      }}>
        Bulletin des notes du {trimestreNom}
      </div>

      {/* INFORMATIONS ÉLÈVE */}
      <div style={{
        backgroundColor: "#F8F9FA",
        padding: nbMatieres > 16 ? "2mm" : "3mm",
        borderRadius: "1mm",
        marginBottom: nbMatieres > 12 ? "3mm" : "5mm",
        fontSize: nbMatieres > 16 ? "8pt" : "9pt",
        border: "1px solid #DEE2E6"
      }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", rowGap: "2mm", columnGap: "6mm" }}>
          
          <div style={{ display: "flex", gap: "2mm", alignItems: "center" }}>
            <strong>Nom:</strong>
            <span style={{ fontWeight: "bold" }}>{eleveNom}</span>
          </div>
          <div style={{ display: "flex", gap: "2mm", alignItems: "center" }}>
            <strong>Année scolaire:</strong>
            <span style={{ fontWeight: "bold" }}>{anneeScolaire}</span>
          </div>

          <div style={{ display: "flex", gap: "2mm", alignItems: "center" }}>
            <strong>Prénom(s):</strong>
            <span style={{ fontWeight: "bold" }}>{elevePrenom}</span>
          </div>
          <div style={{ display: "flex", gap: "2mm", alignItems: "center" }}>
            <strong>Classe:</strong>
            <span style={{ fontWeight: "bold" }}>{classeNom}</span>
          </div>

          <div style={{ display: "flex", gap: "2mm", alignItems: "center" }}>
            <strong>Né(e) le:</strong>
            <span style={{ fontWeight: "bold" }}>{formatDateNaissance(bulletin?.eleve_date_naissance)}</span>
          </div>
          <div style={{ display: "flex", gap: "2mm", alignItems: "center" }}>
            <strong>Période scolaire:</strong>
            <span style={{ fontWeight: "bold" }}>{trimestreNom}</span>
          </div>

        </div>
      </div>

      {/* LIGNE SÉPARATRICE */}
      <div style={{
        borderBottom: "2px dashed #404242b2",
        margin: "4mm 0"
      }}></div>

      {/* TABLEAU DES NOTES */}
      <div style={{ 
        display: "flex", 
        alignItems: "flex-start", 
        gap: nbMatieres > 16 ? "4mm" : "6mm", 
        marginBottom: nbMatieres > 12 ? "3mm" : "5mm"
      }}>
        {/* Tableau des matières */}
        <div style={{ flexGrow: 1 }}>
          <div style={{
            backgroundColor: "#FFFFFF",
            overflow: "hidden",
            border: "1px solid #CCCCCC"
          }}>
            <table style={{
              width: "100%",
              fontSize: tableStyles.fontSize,
              borderCollapse: "collapse",
              tableLayout: "fixed" 
            }}>
              <thead style={printStyle.tableHeader}>
                <tr>
                  <th style={{
                    padding: "2mm",
                    textAlign: "left",
                    fontWeight: "bold",
                    color: "#FFFFFF",
                    textTransform: "uppercase",
                    fontSize: tableStyles.headerFontSize,
                    borderRight: "1px solid #0A7A6F",
                    width: tableStyles.matiereWidth
                  }}>
                    Matières
                  </th>
                  <th style={{
                    padding: "2mm",
                    textAlign: "right",
                    fontWeight: "bold",
                    color: "#FFFFFF",
                    textTransform: "uppercase",
                    fontSize: tableStyles.headerFontSize,
                    width: tableStyles.noteWidth 
                  }}>
                    Note / {noteMaximale}
                  </th>
                </tr>
              </thead>
              <tbody>
                {matieres.map((note, index) => (
                  <tr 
                    key={index}
                    style={{
                      backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#F8F9FA",
                      borderTop: "1px solid #E9ECEF"
                    }}
                  >
                    <td style={{
                      padding: tableStyles.rowPadding,
                      color: "#000000",
                      fontSize: tableStyles.fontSize,
                      borderRight: "1px solid #E9ECEF",
                      lineHeight: "1.2",
                      wordBreak: nbMatieres > 16 ? "break-word" : "normal",
                      width: tableStyles.matiereWidth 
                    }}>
                      {note.matiere_nom}
                    </td>
                    <td style={{
                      padding: tableStyles.rowPadding,
                      textAlign: "right",
                      fontFamily: "'Times New Roman', 'Roboto', serif",
                      fontWeight: "bold",
                      fontVariantNumeric: "normal",
                      color: "#000000",
                      fontSize: tableStyles.fontSize,
                      lineHeight: "1.2",
                      width: tableStyles.noteWidth 
                    }}>
                      {note.displayNote}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{
            marginTop: "2mm",
            display: "flex",
            justifyContent: "space-between",
            fontWeight: "bold",
            fontSize: "12pt",
            textTransform: "uppercase"
          }}>
            <span style={{ 
              backgroundColor: "#0D9488",
              color: "#FFFFFF",
              padding: "2px 4px",
              borderRadius: "2px"
            }}>
              Moyenne Générale
            </span>
            <span style={{ color: "#000000" }}>
              {moyenneFormatee} / {noteMaximale}
            </span>
          </div>
        </div>

        {/* Moyenne virtuelle */}
        <div style={{ 
          flexShrink: 0, 
          width: `${parseFloat(moyenneVirtuelleStyles.width) + 20}mm`
        }}>
          <div style={{
            backgroundColor: "#0D9488",
            color: "#FFFFFF",
            padding: "4mm",
            textAlign: "center",
            border: "1px solid #0D9488",
            borderRadius: "4px 4px 0 0"
          }}>
            <h4 style={{
              fontSize: moyenneVirtuelleStyles.titleFontSize,
              fontWeight: "bold",
              textTransform: "uppercase",
              margin: 0,
              lineHeight: "1.2"
            }}>
              Moyenne du {sousTrimestre}
            </h4>
          </div>
          <div style={{
            border: "1px solid #CCCCCC",
            borderTop: "none",
            textAlign: "center",
            padding: "4mm",
            borderRadius: "0 0 4px 4px"
          }}>
            <p style={{
              fontSize: `${parseFloat(moyenneVirtuelleStyles.moyenneFontSize) + 6}pt`,
              fontWeight: "bold",
              color: "#0D9488",
              margin: 0,
              lineHeight: "1.2"
            }}>
              {moyenneFormatee}
            </p>
          </div>
        </div>
      </div>

      {/* APPRÉCIATIONS */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: nbMatieres > 16 ? "3mm" : "4mm",
        marginBottom: "4mm"
      }}>
        <div style={{
          backgroundColor: "#FFFFFF",
          padding: "3mm",
          border: "1px solid #CCCCCC",
          borderTop: "3px solid #0D9488",
          minHeight: appreciationStyles.minHeight
        }}>
          <h4 style={{
            fontWeight: "bold",
            fontSize: nbMatieres > 16 ? "9pt" : "10pt",
            color: "#000000",
            margin: "0 0 1mm 0",
            paddingBottom: "0.5mm",
            borderBottom: "1px solid #E9ECEF",
            lineHeight: "1.2"
          }}>
            Appréciation de l'éducateur
          </h4>
          <p style={{
            color: "#333333",
            fontSize: appreciationStyles.fontSize,
            margin: 0,
            lineHeight: "1.3"
          }}>
            {getAppreciationEducateur()}
          </p>
        </div>
        
        <div style={{
          backgroundColor: "#FFFFFF",
          padding: "3mm",
          border: "1px solid #CCCCCC",
          borderTop: "3px solid #0D9488",
          minHeight: appreciationStyles.minHeight
        }}>
          <h4 style={{
            fontWeight: "bold",
            fontSize: nbMatieres > 16 ? "9pt" : "10pt",
            color: "#000000",
            margin: "0 0 1mm 0",
            paddingBottom: "0.5mm",
            borderBottom: "1px solid #E9ECEF",
            lineHeight: "1.2"
          }}>
            Appréciation de l'école
          </h4>
          <p style={{
            color: "#333333",
            fontSize: appreciationStyles.fontSize,
            margin: 0,
            lineHeight: "1.3"
          }}>
            {getAppreciationEcole()}
          </p>
        </div>
      </div>

      {/* PIED DE PAGE */}
      <footer style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginTop: nbMatieres > 12 ? "2mm" : "5mm",
        paddingTop: "2mm",
        borderTop: "1px solid #0D9488",
        position: "absolute",
        bottom: "10mm",
        left: "10mm",
        right: "10mm",
        fontSize: "8pt",
        lineHeight: "1.2"
      }}>
        {/* Gauche */}
        <div style={{ textAlign: "left", color: "#0D9488", fontWeight: "bold" }}>
          {sloganEcole}
        </div>

        {/* Centre */}
        <div style={{ textAlign: "center" }}>
          <p style={{
            fontSize: "8pt",
            color: "#666666",
            margin: "0 0 0.5mm 0",
            fontStyle: "italic"
          }}>
            Document officiel de l'école
          </p>
          <p style={{
            fontSize: "7pt",
            color: "#999999",
            margin: 0
          }}>
            Édité le {dateEdition}
          </p>
        </div>

        {/* Droite */}
        <div style={{ textAlign: "right", color: "#0D9488" }}>
          <p style={{ margin: 0, fontWeight: "bold" }}>{siteWeb}</p>
          <p style={{ margin: 0 }}>{emailEcole}</p>
        </div>
      </footer>
    </div>
  );
};

export default BulletinCard;